import type { Dispatch, PropsWithChildren, SetStateAction } from 'react'
import { createContext, useContext, useEffect, useRef, useState } from 'react'
import { useViewportWidth } from '@xstyled/styled-components'

export interface DashboardContextValues {
  showSidebar: boolean
  setShowSidebar: Dispatch<SetStateAction<boolean>>
  toggleSidebar(): void
  getShouldSidebarCollapse(): boolean
}

export const DashboardContext = createContext<DashboardContextValues | null>(
  null
)

export const DashboardContextProvider = (props: PropsWithChildren) => {
  const viewport = useViewportWidth()
  const isMobileView = Boolean(viewport && viewport <= 920)
  const [showSidebar, setShowSidebar] = useState(!isMobileView)
  const previousValue = useRef(isMobileView)

  useEffect(() => {
    if (previousValue.current !== isMobileView) {
      setShowSidebar(!isMobileView)
      previousValue.current = isMobileView
    }
  }, [isMobileView])

  function toggleSidebar() {
    setShowSidebar(!showSidebar)
  }

  function getShouldSidebarCollapse() {
    return isMobileView
  }

  return (
    <DashboardContext.Provider
      value={{
        showSidebar,
        setShowSidebar,
        toggleSidebar,
        getShouldSidebarCollapse
      }}
    >
      {props.children}
    </DashboardContext.Provider>
  )
}

export function useDashboardContext() {
  const context = useContext(DashboardContext)

  if (!context) {
    throw new Error(
      'Attempted to call useDashboardContext outside the Dashboard component.'
    )
  }

  return context
}
